import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

export default function HeroSlider() {
    const [sliders, setSliders] = useState([]);
    const [current, setCurrent] = useState(0);

    useEffect(() => {
        api.get('/sliders').then(({ data }) => setSliders(data)).catch(() => {});
    }, []);

    // Preload the first hero image for a faster LCP
    useEffect(() => {
        if (!sliders[0]?.image) return;
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = sliders[0].image;
        link.fetchPriority = 'high';
        document.head.appendChild(link);
        return () => { try { document.head.removeChild(link); } catch (_) {} };
    }, [sliders]);

    const next = useCallback(() => setCurrent(c => (c + 1) % Math.max(sliders.length, 1)), [sliders.length]);
    const prev = useCallback(() => setCurrent(c => (c - 1 + sliders.length) % Math.max(sliders.length, 1)), [sliders.length]);

    useEffect(() => {
        if (sliders.length <= 1) return;
        const timer = setInterval(next, 5000);
        return () => clearInterval(timer);
    }, [sliders.length, next]);

    if (!sliders.length) return (
        <div className="relative w-full h-[360px] sm:h-[440px] lg:h-[520px] bg-gradient-to-r from-pink-50 to-pink-100 flex items-center justify-center">
            <div className="text-center px-6">
                <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">Glow Beautifully,<br /><span className="text-pink-600">Naturally</span></h2>
                <p className="font-body text-base sm:text-lg text-gray-600 mb-6 max-w-md mx-auto">Premium skincare products for every skin type</p>
                <Link to="/shop"><Button className="bg-pink-600 hover:bg-pink-700 rounded-full px-8 py-3 text-base font-body" data-testid="hero-shop-btn">Shop Now</Button></Link>
            </div>
        </div>
    );

    return (
        <div className="relative w-full h-[360px] sm:h-[440px] lg:h-[520px] overflow-hidden" data-testid="hero-slider">
            {sliders.map((slide, i) => (
                <div key={slide.id} className={`absolute inset-0 transition-all duration-700 ease-in-out ${i === current ? 'opacity-100 translate-x-0' : i < current ? 'opacity-0 -translate-x-full' : 'opacity-0 translate-x-full'}`}>
                    <div className="absolute inset-0 bg-black/30 z-10" />
                    <img src={slide.image} alt={slide.title} className="w-full h-full object-cover"
                         loading={i === 0 ? 'eager' : 'lazy'}
                         fetchPriority={i === 0 ? 'high' : 'auto'}
                         decoding="async" />
                    <div className="absolute inset-0 z-20 flex items-center">
                        <div className="max-w-7xl mx-auto px-6 w-full">
                            <div className="max-w-xl animate-fade-in-up">
                                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 drop-shadow-lg leading-tight">{slide.title}</h2>
                                <p className="font-body text-base sm:text-lg text-white/90 mb-6 drop-shadow">{slide.subtitle}</p>
                                {slide.cta_text && (
                                    <Link to={slide.cta_link || '/shop'}>
                                        <Button className="bg-pink-600 hover:bg-pink-700 rounded-full px-8 py-3 text-base font-body shadow-lg" data-testid="hero-cta-btn">{slide.cta_text}</Button>
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ))}
            {sliders.length > 1 && (
                <>
                    <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow transition-all" data-testid="hero-prev"><ChevronLeft className="w-5 h-5" /></button>
                    <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow transition-all" data-testid="hero-next"><ChevronRight className="w-5 h-5" /></button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex gap-2">
                        {sliders.map((_, i) => (
                            <button key={i} onClick={() => setCurrent(i)} className={`w-2.5 h-2.5 rounded-full transition-all ${i === current ? 'bg-pink-600 w-7' : 'bg-white/60'}`} data-testid={`hero-dot-${i}`} />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
